import { useEffect, useRef, useState } from 'react';
import { Marker } from '../components/ItemsMap/types';

const isSameMarker = (a: Marker | undefined, b: Marker): boolean => {
  if (!a) return false;
  return (
    a.position.lat === b.position.lat &&
    a.position.lng === b.position.lng &&
    a.label === b.label
  );
};

/**
 * Accumulates and stabilizes markers for the current zoom level.
 * Prevents full marker replacement and preserves object references
 * to avoid unnecessary overlay re-renders.
 */
export const useAccumulatedMapMarkers = <Filter>(
  preparedMarkers: Marker[] | undefined,
  zoom: number | undefined,
  filter?: Filter,
  loading?: boolean
) => {
  const cacheRef = useRef<Map<string, Marker>>(new Map());
  const zoomRef = useRef<number | undefined>(zoom);
  const filterRef = useRef<Filter | undefined>(filter);
  const [markers, setMarkers] = useState<Marker[]>([]);

  useEffect(() => {
    const zoomChanged = zoomRef.current !== zoom;
    const filterChanged = filterRef.current !== filter;

    if (zoomChanged || filterChanged) {
      cacheRef.current.clear();
      zoomRef.current = zoom;
      filterRef.current = filter;

      if (loading) {
        setMarkers([]);
        return;
      }
    } else if (loading) {
      return;
    }

    if (!preparedMarkers?.length) {
      if (zoomChanged || filterChanged) setMarkers([]);
      return;
    }

    let changed = zoomChanged || filterChanged;
    const currentCache = cacheRef.current;

    preparedMarkers.forEach(marker => {
      const existing = currentCache.get(marker.id);

      if (!isSameMarker(existing, marker)) {
        currentCache.set(marker.id, marker);
        changed = true;
      }
    });

    if (changed || !markers.length) {
      setMarkers(Array.from(currentCache.values()));
    }
  }, [preparedMarkers, zoom, filter, loading]);

  return markers;
};
